








































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import { AccountResource, CvValue, Resource } from '@/models'

const CATEGORY_CV = 'rsca'
const STATUS_CV = 'rsst'

/**
 * Manages the resources (software / licences / hardware) an account needs and their status.
 * Self-contained: loads the catalog and this account's assignments, and does its own CRUD.
 */
@Component
export default class AccountResources extends Vue {
  @Prop({ default: '' }) accountId!: string

  private resources: Resource[] = []
  private assignments: AccountResource[] = []
  private statuses: CvValue[] = []
  private categories: CvValue[] = []
  private newResourceId: number | null = null
  private newStatus = ''
  private loading = false
  private saving = false

  private fields = [
    { key: 'resource', label: this.$t('account_resources.columns.resource'), sortable: false },
    { key: 'category', label: this.$t('account_resources.columns.category'), sortable: false },
    { key: 'status', label: this.$t('account_resources.columns.status'), sortable: false },
    { key: 'actions', label: this.$t('table.actions'), sortable: false, class: 'text-center' }
  ]

  get availableResources(): Resource[] {
    const assignedIds = this.assignments.map(a => a.resourceId)
    return this.resources.filter(r => r.active !== false && !assignedIds.includes(r.id as number))
  }

  @Watch('accountId')
  async onAccountChange() {
    await this.load()
  }

  async mounted() {
    await this.loadCatalog()
    await this.load()
  }

  private async loadCatalog() {
    try {
      // noCache on the CV lists: bust the long Cache-Control so newly seeded values appear.
      const [resources, statuses, categories] = await Promise.all([
        ApiClient.getResources(),
        ApiClient.getCvValuesByType(STATUS_CV, true),
        ApiClient.getCvValuesByType(CATEGORY_CV, true)
      ])
      this.resources = resources
      this.statuses = statuses
      this.categories = categories
      if (!this.newStatus && statuses.length) this.newStatus = statuses[0].codeId
    } catch (error) {
      console.error('Error loading resource catalog:', error)
    }
  }

  private async load() {
    if (!this.accountId) {
      this.assignments = []
      return
    }
    try {
      this.loading = true
      this.assignments = await ApiClient.getAccountResources(this.accountId)
    } catch (error) {
      console.error('Error loading account resources:', error)
      this.showError(this.$t('account_resources.error_loading').toString())
    } finally {
      this.loading = false
    }
  }

  resourceName(id: number): string {
    return this.resources.find(r => r.id === id)?.name || String(id)
  }

  resourceCategory(id: number): string {
    const cat = this.resources.find(r => r.id === id)?.category
    return this.categories.find(c => c.codeId === cat)?.name || cat || '-'
  }

  async addAssignment() {
    if (!this.newResourceId || !this.newStatus || this.saving) return
    try {
      this.saving = true
      await ApiClient.createAccountResource({
        accountId: this.accountId,
        resourceId: this.newResourceId,
        status: this.newStatus
      })
      this.newResourceId = null
      await this.load()
      this.showSuccess(this.$t('account_resources.added_success').toString())
    } catch (error: any) {
      console.error('Error assigning resource:', error)
      this.showError(error.message || this.$t('account_resources.error_saving').toString())
    } finally {
      this.saving = false
    }
  }

  async changeStatus(assignment: AccountResource, status: string) {
    if (!status || status === assignment.status || assignment.id == null) return
    try {
      await ApiClient.updateAccountResource(assignment.id, { ...assignment, status })
      await this.load()
      this.showSuccess(this.$t('account_resources.updated_success').toString())
    } catch (error: any) {
      console.error('Error updating status:', error)
      this.showError(error.message || this.$t('account_resources.error_saving').toString())
    }
  }

  async removeAssignment(assignment: AccountResource) {
    if (assignment.id == null) return
    const confirmed = await this.$bvModal.msgBoxConfirm(
      this.$t('account_resources.confirm_delete', { name: this.resourceName(assignment.resourceId) }).toString(),
      {
        title: this.$t('account_resources.confirm_delete_title').toString(),
        okVariant: 'danger',
        okTitle: this.$t('button_actions.remove').toString(),
        cancelTitle: this.$t('button_actions.cancel').toString(),
        centered: true
      }
    )
    if (!confirmed) return
    try {
      await ApiClient.deleteAccountResource(assignment.id)
      await this.load()
      this.showSuccess(this.$t('account_resources.deleted_success').toString())
    } catch (error: any) {
      console.error('Error removing resource:', error)
      this.showError(error.message || this.$t('account_resources.error_saving').toString())
    }
  }

  private showSuccess(message: string) {
    this.$bvToast.toast(message, {
      title: this.$t('feedback.feedback_message_success_title').toString(),
      variant: 'success',
      solid: true,
      autoHideDelay: 2000
    })
  }

  private showError(message: string) {
    this.$bvToast.toast(message, {
      title: this.$t('feedback.error_title_generic').toString(),
      variant: 'danger',
      solid: true
    })
  }
}
